import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AnimationBehavior } from '@freelancer/directives';
import { Localization } from '@freelancer/localization';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import {
  LinkColor,
  LinkHoverColor,
  LinkIconPosition,
  LinkUnderline,
  LinkWeight,
} from '@freelancer/ui/link';
import {
  PictureLayout,
  PictureLoadMethod,
  PictureObjectFit,
} from '@freelancer/ui/picture';
import { TextSize } from '@freelancer/ui/text';

@Component({
  standalone: false,
  selector: 'app-home-page-ai-services',
  template: `
    <div
      class="Container mb-xxlarge desktop:mb-xxxxlarge"
      [flAnimateOnScroll]="true"
      [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
      [flAnimateOnScrollDuration]="0.3"
    >
      <fl-picture
        class="Cubes"
        [height]="726"
        [layout]="PictureLayout.RESPONSIVE"
        [loadMethod]="PictureLoadMethod.LAZY"
        [src]="'home/ai-services/cubes.png'"
        [width]="986"
      ></fl-picture>
      <div class="Content">
        <div class="ArtificialIntelligence mb-mid">
          <div class="ArtificialIntelligence-image-container">
            <fl-picture
              class="ArtificialIntelligence-image"
              [height]="2763"
              [layout]="PictureLayout.FILL"
              [loadMethod]="PictureLoadMethod.LAZY"
              [objectFit]="PictureObjectFit.COVER"
              [src]="'home/ai-services/background-v2.jpg'"
              [width]="3687"
            ></fl-picture>
          </div>
          <div class="ArtificialIntelligence-content mb-xxlarge tablet:mb-none">
            <div class="ArtificialIntelligence-content-container">
              <ng-container i18n="AI services homepage header">
                <p
                  class="text-light text-body-20 ArtificialIntelligence-content-text ArtificialIntelligence-content-text-automate desktop:text-title-40 tablet:text-body-24 font-bold mb-none"
                >
                  Automate your calls with
                </p>
                <p
                  class="text-light text-title-60 ArtificialIntelligence-content-text ArtificialIntelligence-content-text-ai desktop:text-title-90 font-black mb-xxsmall desktop:mb-small"
                >
                  AI Agents
                </p>
              </ng-container>
              <ng-container i18n="AI services homepage description text">
                <p
                  class="text-light text-body-16 ArtificialIntelligence-content-text tablet:text-body-20 font-normal mb-small"
                >
                  Imagine you can get AI to answer the phone, make sales, set
                  appointments, and generally make your life way easier. It'll
                  cost you less than building a website, and it's the next big
                  thing for businesses in 2025.
                </p>
                <p
                  class="text-light text-body-16 ArtificialIntelligence-content-text tablet:text-body-20 font-normal mb-mid"
                >
                  Thanks to our extensive global talent network, Freelancer is
                  your best bet for finding AI experts to take your business to
                  the next level.
                </p>
              </ng-container>
              <p
                class="text-light text-body-20 ArtificialIntelligence-content-text ArtificialIntelligence-content-text-ready desktop:text-title-32 font-bold mb-xxsmall"
                i18n="Explore AI Services link title"
              >
                Ready to grow your business with AI?
              </p>
              <fl-link
                class="ArtificialIntelligence-content-text"
                flTrackingLabel="ExploreAIServicesTrackingLabel"
                i18n="Explore AI Services link"
                [color]="LinkColor.PRIMARY"
                [hoverColor]="LinkHoverColor.PRIMARY"
                [iconColor]="IconColor.PRIMARY"
                [iconName]="'ui-arrow-right'"
                [iconPosition]="LinkIconPosition.RIGHT"
                [iconSize]="IconSize.MID"
                [link]="'/ai'"
                [size]="TextSize.BODY_20"
                [sizeDesktop]="TextSize.TITLE_32"
                [underline]="LinkUnderline.HOVER"
                [weight]="LinkWeight.BOLD"
              >
                Explore AI Services
              </fl-link>
            </div>
          </div>
          <div
            class="AiDevelopmentHeading mb-xsmall tablet:mb-small"
            i18n="AI Development text"
            [class.AiDevelopmentHeading-newDesign]="isEnglish"
            [class.AiDevelopmentHeading-newDesign-nonEnglish]="!isEnglish"
          >
            <span
              class="text-color-inherit text-body-24 AiText AiText-newDesign-gradientColor tablet:text-title-40 uppercase font-black"
              [class.AiText-newDesign]="isEnglish"
              [class.InlineText]="!isEnglish"
            >
              AI
            </span>
            <div class="DevelopmentText">
              <span
                class="text-light text-body-24 DevelopmentText tablet:text-title-40 uppercase font-black"
              >
                DEVELOPMENT
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./ai-services.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomePageAiServicesComponent implements OnInit {
  private localization = inject(Localization);

  AnimationBehavior = AnimationBehavior;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkColor = LinkColor;
  LinkHoverColor = LinkHoverColor;
  LinkIconPosition = LinkIconPosition;
  LinkUnderline = LinkUnderline;
  LinkWeight = LinkWeight;
  PictureLayout = PictureLayout;
  PictureLoadMethod = PictureLoadMethod;
  PictureObjectFit = PictureObjectFit;
  TextSize = TextSize;

  isEnglish = true;

  ngOnInit(): void {
    this.isEnglish = this.localization.isEnglish();
  }
}
